// Local cache for streamed-SOG chunk files (the per-chunk meta.json + its
// WebP images), so moving around a streamed scene doesn't wait on the
// network for areas already downloaded once — on this visit or a later one.
//
// Uses the Cache Storage API (works in the decode workers too). Entries are
// keyed by the file URL plus the LOD tree's version tag (the ETag of its
// lod-meta.json), so a regenerated tree can never be served stale; older
// versions of the same tree are purged when the tree is opened.
//
// SIZE-BOUNDED. A streamed tree can be far bigger than any device should
// keep (a 200M-splat city scan is ~4 GB), and all of this origin's storage —
// including utils/modelCache.js's one-shot models — is evicted as a unit
// when the browser runs short, so the chunk cache must never balloon:
//   - every cached file is recorded in a small IndexedDB index (bytes, last
//     access, "coarse" flag);
//   - enforceCap() drops least-recently-used files until under the cap,
//     fine-detail files first, coarse-level files (small, needed from every
//     viewpoint) last;
//   - the cap is per device class (chunkCacheCap) and never more than a
//     fifth of the storage the browser says is left.
//
// planPreload()/preloadFiles() decide what to download ahead of need:
// the whole tree only when it comfortably fits (desktop), otherwise just the
// coarse levels, plus (desktop) the detail around the camera.

const CACHE_NAME = 'cinesplat-lod-chunks-v2';
// v1 had no size index, so its entries could never be evicted — dropped.
const OLD_CACHE_NAMES = ['cinesplat-lod-chunks-v1'];
const DB_NAME = 'cinesplat-lod-index';
const STORE = 'chunks';

const MB = 1024 * 1024;
// Per device class: hard ceiling for the whole chunk cache (all trees), and
// the share of it the coarse levels may use (those are protected from
// eviction until nothing else is left, and preloaded).
const DEVICE_LIMITS = {
  phone: { ceiling: 200 * MB, coarseShare: 0.15 },
  tablet: { ceiling: 500 * MB, coarseShare: 0.25 },
  desktop: { ceiling: 2048 * MB, coarseShare: 0.25 },
};
// Never take more than this fraction of the storage the browser reports as
// still available to the origin.
const MAX_SHARE_OF_FREE = 0.2;
// Trim to this fraction of the cap once over it, so a busy session isn't
// evicting on every pass.
const TRIM_TO = 0.9;
// Measured on Concert Hall V1 SS: 241 MB / 12.8M splats ≈ 19 bytes a splat
// (all files incl. SH — the decode worker fetches every referenced file).
const EST_BYTES_PER_SPLAT = 20;
// Don't rewrite a file's last-access time more often than this.
const TOUCH_INTERVAL_MS = 60_000;

const keyFor = (url, tag) => `${url}${url.includes('?') ? '&' : '?'}lodv=${encodeURIComponent(tag)}`;
const hasCaches = () => typeof caches !== 'undefined';

// ─── IndexedDB size index ───────────────────────────────────────────────────

let _dbPromise = null;
function openDb() {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  if (!_dbPromise) {
    _dbPromise = new Promise((resolve) => {
      try {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'key' });
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
        req.onblocked = () => resolve(null);
      } catch (e) { resolve(null); }
    });
  }
  return _dbPromise;
}

const reqP = (req) => new Promise((resolve, reject) => { req.onsuccess = () => resolve(req.result); req.onerror = () => reject(req.error); });

async function withStore(mode, fn) {
  const db = await openDb();
  if (!db) return undefined;
  try {
    const tx = db.transaction(STORE, mode);
    // Hook completion before running fn — the transaction can finish
    // before fn's promise settles.
    const done = new Promise((resolve) => { tx.oncomplete = resolve; tx.onerror = resolve; tx.onabort = resolve; });
    const result = await fn(tx.objectStore(STORE));
    await done;
    return result;
  } catch (e) { return undefined; }
}

const _lastTouch = new Map();
function recordEntry(key, bytes, coarse) {
  _lastTouch.set(key, Date.now());
  return withStore('readwrite', (s) => reqP(s.put({ key, bytes, lastAccess: Date.now(), coarse: !!coarse })));
}

async function touchEntry(key, coarse, hit) {
  const now = Date.now();
  if (now - (_lastTouch.get(key) || 0) < TOUCH_INTERVAL_MS) return;
  _lastTouch.set(key, now);
  const existing = await withStore('readonly', (s) => reqP(s.get(key)));
  // A cached file with no index record (e.g. the index write failed) — measure it.
  const bytes = existing ? existing.bytes : (await hit.clone().arrayBuffer()).byteLength;
  await withStore('readwrite', (s) => reqP(s.put({ key, bytes, lastAccess: now, coarse: !!(coarse || existing?.coarse) })));
}

// ─── Fetch ──────────────────────────────────────────────────────────────────

// Download pass for streamed models: a CloudFront signed query string from
// the backend (getModel's lodQuery), valid for one model's folder for a
// limited time. Added to network requests only — cache keys stay the clean
// URL, so a renewed pass never invalidates what's cached. Decode workers get
// it with each job (their copy of this module has its own state).
let _accessQuery = '';
export function setAccessQuery(query) { _accessQuery = query || ''; }
export function getAccessQuery() { return _accessQuery; }
export const accessUrl = (url, query = _accessQuery) => (query ? `${url}${url.includes('?') ? '&' : '?'}${query}` : url);

// fetch() with a Cache Storage layer. Without a version tag (or without
// Cache Storage) it's a plain fetch — never risks serving stale data.
// `coarse` marks a coarse-level file (evicted last). `onProgress(bytes,
// fileTotal, first)` is called as a network download arrives (not for cache
// hits): `bytes` is the new delta, `first` marks this file's first call.
// Cache Storage calls can stall without settling (iPad Safari) — and a
// stalled lookup or store held the whole chunk, and every chunk queued
// behind it on that worker (see DecodePool's DECODE_STALL_MS). Past this
// limit a lookup counts as a miss and a store is left to finish on its own.
const CACHE_OP_TIMEOUT_MS = 5000;
const withinTime = (promise) => Promise.race([
  promise,
  new Promise((_, reject) => setTimeout(() => reject(new Error('cache timeout')), CACHE_OP_TIMEOUT_MS)),
]);

export async function cachedFetch(url, tag, { coarse = false, onProgress = null, query = _accessQuery } = {}) {
  const netUrl = accessUrl(url, query);
  if (!tag || !hasCaches()) return fetch(netUrl);
  let cache;
  const key = keyFor(url, tag);
  try {
    cache = await withinTime(caches.open(CACHE_NAME));
    const hit = await withinTime(cache.match(key));
    if (hit) {
      touchEntry(key, coarse, hit).catch(() => {});
      return hit;
    }
  } catch (e) {
    return fetch(netUrl);
  }
  const res = await fetch(netUrl);
  if (!res.ok) return res;
  // Read the body once so its size is known for the index; hand the caller
  // an equivalent Response built from the same bytes.
  const buf = await readBody(res, onProgress);
  const out = new Response(buf, { status: res.status, statusText: res.statusText, headers: res.headers });
  try {
    await withinTime(cache.put(key, out.clone()));
    recordEntry(key, buf.byteLength, coarse).catch(() => {});
  } catch (e) { /* quota / private mode — just don't cache */ }
  return out;
}

// Whole body as an ArrayBuffer, streamed so onProgress can follow it.
async function readBody(res, onProgress) {
  if (!onProgress || !res.body) return res.arrayBuffer();
  const total = parseInt(res.headers.get('content-length'), 10) || 0;
  const reader = res.body.getReader();
  const chunks = [];
  let size = 0;
  let first = true;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    size += value.length;
    onProgress(value.length, total, first);
    first = false;
  }
  const out = new Uint8Array(size);
  let o = 0;
  for (const c of chunks) { out.set(c, o); o += c.length; }
  return out.buffer;
}

// ─── Cap & eviction ─────────────────────────────────────────────────────────

export async function cachedBytes() {
  const all = await withStore('readonly', (s) => reqP(s.getAll()));
  return (all || []).reduce((sum, r) => sum + (r.bytes || 0), 0);
}

// Byte cap for the whole chunk cache on this device.
export async function chunkCacheCap(deviceClass) {
  const { ceiling } = DEVICE_LIMITS[deviceClass] || DEVICE_LIMITS.desktop;
  try {
    const est = await navigator.storage?.estimate?.();
    if (est && est.quota) {
      // Our own entries count as available — they're ours to keep or drop.
      const room = est.quota - (est.usage || 0) + await cachedBytes();
      return Math.max(0, Math.min(ceiling, room * MAX_SHARE_OF_FREE));
    }
  } catch (e) { /* no estimate — use the ceiling */ }
  return ceiling;
}

// Drop least-recently-used files until the cache is under `cap`
// (fine-detail first, coarse levels last). Best-effort, never throws.
let _enforcing = null;
export function enforceCap(cap) {
  if (!hasCaches()) return Promise.resolve();
  if (_enforcing) return _enforcing;
  _enforcing = (async () => {
    const all = await withStore('readonly', (s) => reqP(s.getAll()));
    if (!all) return;
    let total = all.reduce((sum, r) => sum + (r.bytes || 0), 0);
    if (total <= cap) return;
    all.sort((a, b) => (a.coarse - b.coarse) || (a.lastAccess - b.lastAccess));
    const target = cap * TRIM_TO;
    const drop = [];
    for (const r of all) {
      if (total <= target) break;
      drop.push(r.key);
      total -= r.bytes || 0;
    }
    const cache = await caches.open(CACHE_NAME);
    for (const key of drop) {
      try { await cache.delete(key); } catch (e) { /* already gone */ }
      _lastTouch.delete(key);
    }
    await withStore('readwrite', (s) => Promise.all(drop.map((key) => reqP(s.delete(key)))));
  })().catch(() => {}).finally(() => { _enforcing = null; });
  return _enforcing;
}

// Called once when a tree is opened: drops the old unindexed cache and any
// other version of this tree.
export async function openTreeCache(treeDir, tag) {
  if (!hasCaches()) return;
  try { await Promise.all(OLD_CACHE_NAMES.map((n) => caches.delete(n))); } catch (e) { /* best-effort */ }
  if (!tag) return;
  try {
    const cache = await caches.open(CACHE_NAME);
    const want = `lodv=${encodeURIComponent(tag)}`;
    const stale = (await cache.keys()).map((r) => r.url).filter((u) => u.startsWith(treeDir) && !u.includes(want));
    for (const url of stale) await cache.delete(url);
    // Index records (including ones whose cache entry already vanished).
    const records = await withStore('readonly', (s) => reqP(s.getAllKeys()));
    const staleKeys = new Set(stale);
    for (const k of records || []) if (k.startsWith(treeDir) && !k.includes(want)) staleKeys.add(k);
    await withStore('readwrite', (s) => Promise.all([...staleKeys].map((k) => reqP(s.delete(k)))));
  } catch (e) { /* best-effort */ }
}

// ─── Preload planning ───────────────────────────────────────────────────────

// Is this connection OK for downloading ahead of need? Save-Data or a slow
// link → no. (navigator.connection only exists on Chromium; elsewhere this
// is true and the device-class limits are what keep it modest.)
export function networkAllowsPrefetch() {
  const c = typeof navigator !== 'undefined' ? navigator.connection : null;
  if (!c) return true;
  if (c.saveData) return false;
  return !['slow-2g', '2g', '3g'].includes(c.effectiveType);
}

// Per-file level + estimated size from lod-meta, and which files are
// "coarse": the coarsest whole levels that together fit the device's
// coarse share of the cap. Decides what to preload:
//   full     — desktop, whole tree ≤ half the cap: everything, coarse first
//   coarse   — the coarse files only (phones/tablets, or a too-big tree)
//   nearBudget — desktop with a too-big tree: bytes it may additionally
//                prefetch around the camera (see nearCameraFiles)
export function planPreload(meta, cap, deviceClass) {
  const { coarseShare } = DEVICE_LIMITS[deviceClass] || DEVICE_LIMITS.desktop;
  const nFiles = meta.filenames.length;
  const fileLevel = new Array(nFiles).fill(0);
  const fileEst = new Array(nFiles).fill(0);
  for (const leaf of meta.leaves) {
    for (const [level, entry] of Object.entries(leaf.lods)) {
      if (!entry || entry.file == null) continue;
      fileLevel[entry.file] = Number(level);
      fileEst[entry.file] += (entry.count || 0) * EST_BYTES_PER_SPLAT;
    }
  }
  const totalEst = fileEst.reduce((a, b) => a + b, 0);

  // Level 0 is the finest; walk from the highest (coarsest) level down.
  const levels = [...new Set(fileLevel)].sort((a, b) => b - a);
  const levelBytes = new Map(levels.map((l) => [l, 0]));
  fileLevel.forEach((l, i) => levelBytes.set(l, levelBytes.get(l) + fileEst[i]));
  const coarseLevels = new Set();
  let coarseBytes = 0;
  for (const l of levels) {
    if (coarseBytes + levelBytes.get(l) > cap * coarseShare) break;
    coarseBytes += levelBytes.get(l);
    coarseLevels.add(l);
  }
  const fileCoarse = fileLevel.map((l) => coarseLevels.has(l));
  const full = deviceClass === 'desktop' && totalEst <= cap * 0.5;
  // Coarse-first order for whatever gets preloaded.
  const byCoarseness = [...fileLevel.keys()].sort((a, b) => fileLevel[b] - fileLevel[a]);
  return {
    fileLevel, fileEst, fileCoarse, totalEst,
    preload: full ? byCoarseness : byCoarseness.filter((i) => fileCoarse[i]),
    nearBudget: !full && deviceClass === 'desktop' ? Math.max(0, cap * 0.5 - coarseBytes) : 0,
  };
}

// Files backing the leaves nearest `camPos` (render space, same as
// meta.leaves[].center), coarse to fine per leaf, until `budget` estimated
// bytes. Coarse files are skipped (already preloaded).
export function nearCameraFiles(meta, plan, camPos, budget) {
  const leaves = meta.leaves
    .map((leaf) => ({ leaf, d: leaf.center.distanceToSquared(camPos) }))
    .sort((a, b) => a.d - b.d);
  const out = [];
  const seen = new Set();
  let used = 0;
  for (const { leaf } of leaves) {
    const entries = Object.entries(leaf.lods).sort((a, b) => Number(b[0]) - Number(a[0]));
    for (const [, entry] of entries) {
      const f = entry?.file;
      if (f == null || seen.has(f) || plan.fileCoarse[f]) continue;
      seen.add(f);
      if (used + plan.fileEst[f] > budget) continue; // too big — a smaller one may fit
      used += plan.fileEst[f];
      out.push(f);
    }
  }
  return out;
}

// Every file a chunk meta.json references (any `files` array, wherever
// nested) — same walk the decode worker does.
function referencedFiles(metaJson) {
  const out = new Set();
  const walk = (node) => {
    if (Array.isArray(node)) { node.forEach(walk); return; }
    if (node && typeof node === 'object') {
      for (const [key, value] of Object.entries(node)) {
        if (key === 'files' && Array.isArray(value)) value.forEach((f) => typeof f === 'string' && out.add(f));
        else walk(value);
      }
    }
  };
  walk(metaJson);
  return [...out];
}

// Downloads the given chunks (indexes into `chunkMetaUrls`, the absolute
// meta.json URLs — parseLodMeta's meta.filenames) into the cache, in order.
// Already-cached files cost nothing. Resolves when done or aborted; never
// throws. `whenFree` (optional): awaited before each chunk — the background
// preload pauses while the visible view still has chunks to stream, so it
// never takes bandwidth from what's on screen.
export async function preloadFiles(chunkMetaUrls, fileIndexes, tag, { fileCoarse = [], concurrency = 3, signal, whenFree } = {}) {
  if (!tag || !hasCaches() || !fileIndexes?.length) return;
  const queue = [...fileIndexes];
  const runOne = async () => {
    while (queue.length && !signal?.aborted) {
      if (whenFree) await whenFree();
      if (signal?.aborted || !queue.length) return;
      const i = queue.shift();
      const metaUrl = chunkMetaUrls[i];
      const coarse = !!fileCoarse[i];
      try {
        const res = await cachedFetch(metaUrl, tag, { coarse });
        if (!res.ok) continue;
        const files = referencedFiles(await res.clone().json());
        const base = metaUrl.slice(0, metaUrl.lastIndexOf('/') + 1);
        for (const name of files) {
          if (signal?.aborted) return;
          await cachedFetch(base + name, tag, { coarse });
        }
      } catch (e) { /* keep going — this is only a warm-up */ }
    }
  };
  await Promise.all(Array.from({ length: concurrency }, runOne));
}
